/**
 * SFrame146 — Financial Accounts & Assets (secure-inventory si-1)
 * Consolidated page: Discovery checklist, Financial Accounts CRUD, Tax Documents CRUD.
 *
 * Persistence note: saveFrameData REPLACES the entire frame data object (server-side upsert).
 * All mutations use setFrameData with a full snapshot so no sibling state is ever wiped.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SettlementStepPage } from '../../settlement';
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import Modal from '../../ui/Modal';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  toDocumentUploadRow,
  uploadSettlementDocument,
  type SettlementDocumentRow,
} from '../../../utils/settlementDocuments';

const step = SETTLEMENT_PHASE_CONFIGS['secure-inventory'].steps.find((s) => s.id === 'si-1')!;
const PHASE = 'secure-inventory' as const;
const STEP_ID = step.id;
const FRAME_ID = 'sframe-146';

// ── Types ──────────────────────────────────────────────────────────────────

type FinancialAccountCategory =
  | 'cash_deposit'
  | 'investment'
  | 'retirement'
  | 'income_source'
  | 'other_financial';

type ValuationMethod = 'statement_balance' | 'estimated' | 'market_value' | 'appraised' | 'unknown';
type AccessStatus = 'has_access' | 'no_access' | 'unknown';
type IntendedAction = 'transfer' | 'close' | 'claim' | 'hold' | 'unknown';
type ExecutorProgressStatus = 'needs_review' | 'in_progress' | 'completed';

interface SettlementFinancialAccount {
  id: string;
  category: FinancialAccountCategory;
  type: string;
  name: string;
  institution?: string;
  lastFour?: string;
  value?: number;
  valuationMethod: ValuationMethod;
  accessStatus: AccessStatus;
  intendedAction: IntendedAction;
  executorProgressStatus: ExecutorProgressStatus;
  notes?: string;
  documentIds: string[];
}

type TaxDocumentCategory = 'federal' | 'state' | 'property' | 'other';
type TaxDocumentStatus = 'filed' | 'not_filed' | 'unknown';
type TaxExecutorStatus = 'needs_review' | 'verified' | 'missing';

interface SettlementTaxDocument {
  id: string;
  category: TaxDocumentCategory;
  year: string;
  description: string;
  status: TaxDocumentStatus;
  amount?: number;
  notes?: string;
  executorStatus: TaxExecutorStatus;
  documentIds: string[];
}

interface DiscoveryChecklist {
  reviewTaxReturns: boolean;
  checkMailEmail: boolean;
  searchDeviceApps: boolean;
  lookForDirectDeposits: boolean;
  contactInstitutions: boolean;
}

interface SFrame146FrameData {
  financialAccounts: SettlementFinancialAccount[];
  taxDocuments: SettlementTaxDocument[];
  discoveryChecklist: DiscoveryChecklist;
}

// ── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<FinancialAccountCategory, string> = {
  cash_deposit: 'Cash and Deposit Accounts',
  investment: 'Investment Accounts',
  retirement: 'Retirement Accounts',
  income_source: 'Other Income Sources',
  other_financial: 'Other Financial Assets',
};

const CATEGORY_ORDER: FinancialAccountCategory[] = [
  'cash_deposit',
  'investment',
  'retirement',
  'income_source',
  'other_financial',
];

const TYPE_OPTIONS: Record<FinancialAccountCategory, { value: string; label: string }[]> = {
  cash_deposit: [
    { value: 'checking', label: 'Checking Account' },
    { value: 'savings', label: 'Savings Account' },
    { value: 'money_market', label: 'Money Market Account' },
    { value: 'cd', label: 'Certificate of Deposit' },
    { value: 'other', label: 'Other Account' },
  ],
  investment: [
    { value: 'stocks', label: 'Stocks / Brokerage' },
    { value: 'bonds', label: 'Bonds' },
    { value: 'mutual_funds', label: 'Mutual Funds' },
    { value: 'etfs', label: 'ETFs' },
    { value: 'other', label: 'Other Investment' },
  ],
  retirement: [
    { value: 'traditional_ira', label: 'Traditional IRA' },
    { value: '401k', label: '401(k)' },
    { value: 'roth_ira', label: 'Roth IRA' },
    { value: 'pension', label: 'Pension' },
    { value: 'other', label: 'Other Retirement Account' },
  ],
  income_source: [
    { value: 'social_security', label: 'Social Security' },
    { value: 'pension', label: 'Pension' },
    { value: 'rental_income', label: 'Rental Income' },
    { value: 'investment_income', label: 'Investment Income' },
    { value: 'business_income', label: 'Business Income' },
    { value: 'annuity', label: 'Annuity' },
    { value: 'other', label: 'Other Income' },
  ],
  other_financial: [
    { value: 'life_insurance', label: 'Life Insurance' },
    { value: 'annuity', label: 'Annuity' },
    { value: 'trust', label: 'Trust' },
    { value: 'business', label: 'Business Interest' },
    { value: 'other', label: 'Other Financial Asset' },
  ],
};

const TAX_CATEGORY_ORDER: TaxDocumentCategory[] = ['federal', 'state', 'property', 'other'];

const TAX_CATEGORY_LABELS: Record<TaxDocumentCategory, string> = {
  federal: 'Federal Tax Returns',
  state: 'State Tax Returns',
  property: 'Property Tax Records',
  other: 'Other Tax Documents',
};

const DISCOVERY_ITEMS: { key: keyof DiscoveryChecklist; label: string }[] = [
  { key: 'reviewTaxReturns', label: 'Review recent tax returns (1099s, interest, dividends)' },
  { key: 'checkMailEmail', label: 'Check mail and email for account statements or notifications' },
  { key: 'searchDeviceApps', label: 'Search for banking and investment apps on devices' },
  { key: 'lookForDirectDeposits', label: 'Look for direct deposits (Social Security, pension, income sources)' },
  { key: 'contactInstitutions', label: 'Contact known financial institutions to confirm accounts' },
];

// ── Defaults ────────────────────────────────────────────────────────────────

function defaultDiscovery(): DiscoveryChecklist {
  return {
    reviewTaxReturns: false,
    checkMailEmail: false,
    searchDeviceApps: false,
    lookForDirectDeposits: false,
    contactInstitutions: false,
  };
}

function defaultFrameData(): SFrame146FrameData {
  return {
    financialAccounts: [],
    taxDocuments: [],
    discoveryChecklist: defaultDiscovery(),
  };
}

function defaultAcctForm(category: FinancialAccountCategory): SettlementFinancialAccount {
  return {
    id: crypto.randomUUID(),
    category,
    type: TYPE_OPTIONS[category][0].value,
    name: '',
    institution: '',
    lastFour: '',
    valuationMethod: 'statement_balance',
    accessStatus: 'unknown',
    intendedAction: 'unknown',
    executorProgressStatus: 'needs_review',
    notes: '',
    documentIds: [],
  };
}

function defaultTaxForm(category: TaxDocumentCategory): SettlementTaxDocument {
  return {
    id: crypto.randomUUID(),
    category,
    year: new Date().getFullYear().toString(),
    description: '',
    status: 'unknown',
    executorStatus: 'needs_review',
    documentIds: [],
  };
}

// ── Label helpers ────────────────────────────────────────────────────────────

function valuationLabel(v: ValuationMethod) {
  const map: Record<ValuationMethod, string> = {
    statement_balance: 'Statement Balance',
    estimated: 'Estimated',
    market_value: 'Market Value',
    appraised: 'Appraised',
    unknown: 'Unknown',
  };
  return map[v];
}

function accessLabel(v: AccessStatus) {
  const map: Record<AccessStatus, string> = {
    has_access: 'Has Access',
    no_access: 'No Access',
    unknown: 'Unknown',
  };
  return map[v];
}

function actionLabel(v: IntendedAction) {
  const map: Record<IntendedAction, string> = {
    transfer: 'Transfer',
    close: 'Close',
    claim: 'Claim',
    hold: 'Hold',
    unknown: 'Unknown',
  };
  return map[v];
}

function progressLabel(v: ExecutorProgressStatus) {
  const map: Record<ExecutorProgressStatus, string> = {
    needs_review: 'Needs Review',
    in_progress: 'In Progress',
    completed: 'Completed',
  };
  return map[v];
}

function progressBadgeClass(v: ExecutorProgressStatus) {
  if (v === 'completed') return 'border-emerald-300 bg-emerald-50 text-emerald-900';
  if (v === 'in_progress') return 'border-amber-300 bg-amber-50 text-amber-900';
  return 'border-stone-200 bg-stone-100 text-stone-700';
}

function taxStatusLabel(v: TaxDocumentStatus) {
  const map: Record<TaxDocumentStatus, string> = {
    filed: 'Filed',
    not_filed: 'Not Filed',
    unknown: 'Unknown',
  };
  return map[v];
}

function taxExecutorLabel(v: TaxExecutorStatus) {
  const map: Record<TaxExecutorStatus, string> = {
    needs_review: 'Needs Review',
    verified: 'Verified',
    missing: 'Missing',
  };
  return map[v];
}

function taxExecutorBadgeClass(v: TaxExecutorStatus) {
  if (v === 'verified') return 'border-emerald-300 bg-emerald-50 text-emerald-900';
  if (v === 'missing') return 'border-red-300 bg-red-50 text-red-900';
  return 'border-stone-200 bg-stone-100 text-stone-700';
}

function typeLabel(category: FinancialAccountCategory, typeValue: string): string {
  return TYPE_OPTIONS[category].find((o) => o.value === typeValue)?.label ?? typeValue;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function SFrame146Accounts() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId ?? null, { readOnly: isArchived });

  // Unified frame state — always saved as a complete snapshot to avoid partial overwrites
  const [frameData, setFrameData] = useState<SFrame146FrameData>(defaultFrameData);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [allStepDocs, setAllStepDocs] = useState<SettlementDocumentRow[]>([]);

  // Financial account modal
  const [acctModalOpen, setAcctModalOpen] = useState(false);
  const [acctForm, setAcctForm] = useState<SettlementFinancialAccount | null>(null);
  const [isEditingAcct, setIsEditingAcct] = useState(false);

  // Tax document modal
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [taxForm, setTaxForm] = useState<SettlementTaxDocument | null>(null);
  const [isEditingTax, setIsEditingTax] = useState(false);

  // ── Load ──

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    (async () => {
      const fd = await loadFrameData(FRAME_ID);
      if (cancelled) return;
      const d = (fd?.data ?? {}) as Partial<SFrame146FrameData>;
      setFrameData({
        financialAccounts: Array.isArray(d.financialAccounts) ? d.financialAccounts : [],
        taxDocuments: Array.isArray(d.taxDocuments) ? d.taxDocuments : [],
        discoveryChecklist: { ...defaultDiscovery(), ...(d.discoveryChecklist ?? {}) },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  // ── Documents ──

  const refreshDocs = useCallback(async () => {
    if (!estateId) return;
    const rows = await listSettlementDocuments(estateId, PHASE, STEP_ID);
    setAllStepDocs(rows);
  }, [estateId]);

  useEffect(() => {
    void refreshDocs();
  }, [refreshDocs]);

  const docsByIds = useCallback(
    (ids: string[]) => {
      const wanted = new Set(ids);
      return allStepDocs.filter((d) => wanted.has(d.id)).map(toDocumentUploadRow);
    },
    [allStepDocs]
  );

  // ── Persist helper (always saves full snapshot) ──

  const persistSnapshot = useCallback(
    (next: SFrame146FrameData) => {
      if (!estateId || isArchived) return;
      void saveFrameData(FRAME_ID, next as unknown as Record<string, unknown>);
    },
    [estateId, isArchived, saveFrameData]
  );

  // ── Discovery ──

  const toggleDiscovery = (key: keyof DiscoveryChecklist) => {
    if (isArchived) return;
    setFrameData((prev) => {
      const next: SFrame146FrameData = {
        ...prev,
        discoveryChecklist: { ...prev.discoveryChecklist, [key]: !prev.discoveryChecklist[key] },
      };
      persistSnapshot(next);
      return next;
    });
  };

  // ── Financial accounts ──

  const openNewAcct = (category: FinancialAccountCategory) => {
    setAcctForm(defaultAcctForm(category));
    setIsEditingAcct(false);
    setAcctModalOpen(true);
  };

  const openEditAcct = (acct: SettlementFinancialAccount) => {
    setAcctForm({ ...acct });
    setIsEditingAcct(true);
    setAcctModalOpen(true);
  };

  const closeAcctModal = () => {
    setAcctModalOpen(false);
    setAcctForm(null);
    setIsEditingAcct(false);
  };

  const saveAcct = () => {
    if (!acctForm || !acctForm.name.trim()) return;
    setFrameData((prev) => {
      const next: SFrame146FrameData = {
        ...prev,
        financialAccounts: isEditingAcct
          ? prev.financialAccounts.map((a) => (a.id === acctForm.id ? acctForm : a))
          : [...prev.financialAccounts, acctForm],
      };
      persistSnapshot(next);
      return next;
    });
    closeAcctModal();
  };

  const deleteAcct = (id: string) => {
    setFrameData((prev) => {
      const next: SFrame146FrameData = {
        ...prev,
        financialAccounts: prev.financialAccounts.filter((a) => a.id !== id),
      };
      persistSnapshot(next);
      return next;
    });
  };

  // ── Tax documents ──

  const openNewTax = (category: TaxDocumentCategory) => {
    setTaxForm(defaultTaxForm(category));
    setIsEditingTax(false);
    setTaxModalOpen(true);
  };

  const openEditTax = (doc: SettlementTaxDocument) => {
    setTaxForm({ ...doc });
    setIsEditingTax(true);
    setTaxModalOpen(true);
  };

  const closeTaxModal = () => {
    setTaxModalOpen(false);
    setTaxForm(null);
    setIsEditingTax(false);
  };

  const saveTax = () => {
    if (!taxForm || !taxForm.description.trim() || !taxForm.year.trim()) return;
    setFrameData((prev) => {
      const next: SFrame146FrameData = {
        ...prev,
        taxDocuments: isEditingTax
          ? prev.taxDocuments.map((d) => (d.id === taxForm.id ? taxForm : d))
          : [...prev.taxDocuments, taxForm],
      };
      persistSnapshot(next);
      return next;
    });
    closeTaxModal();
  };

  const deleteTax = async (id: string) => {
    // Delete all attached settlement documents first to avoid orphans
    const doc = frameData.taxDocuments.find((d) => d.id === id);
    if (doc && doc.documentIds.length > 0 && estateId) {
      await Promise.all(doc.documentIds.map((docId) => deleteSettlementDocument(estateId, docId)));
      await refreshDocs();
    }
    setFrameData((prev) => {
      const next: SFrame146FrameData = {
        ...prev,
        taxDocuments: prev.taxDocuments.filter((d) => d.id !== id),
      };
      persistSnapshot(next);
      return next;
    });
  };

  // Tax document file upload — taxForm always has an id (pre-generated for new docs)
  const handleTaxDocUpload = async (file: File) => {
    if (!taxForm || !estateId || isArchived) return;
    const uploaded = await uploadSettlementDocument({
      estateId,
      phase: PHASE,
      stepId: STEP_ID,
      file,
      description: `tax:${taxForm.id}`,
    });
    await refreshDocs();
    const newDocId = uploaded.id;
    // Update modal form state
    setTaxForm((prev) => prev && { ...prev, documentIds: [...prev.documentIds, newDocId] });
    // If editing an existing doc, persist immediately
    if (isEditingTax) {
      setFrameData((prev) => {
        const next: SFrame146FrameData = {
          ...prev,
          taxDocuments: prev.taxDocuments.map((d) =>
            d.id === taxForm.id ? { ...d, documentIds: [...d.documentIds, newDocId] } : d
          ),
        };
        persistSnapshot(next);
        return next;
      });
    }
  };

  const handleTaxDocDelete = async (docId: string) => {
    if (!taxForm || !estateId || isArchived) return;
    await deleteSettlementDocument(estateId, docId);
    await refreshDocs();
    // Update modal form state
    setTaxForm((prev) =>
      prev && { ...prev, documentIds: prev.documentIds.filter((x) => x !== docId) }
    );
    // If editing an existing doc, persist immediately
    if (isEditingTax) {
      setFrameData((prev) => {
        const next: SFrame146FrameData = {
          ...prev,
          taxDocuments: prev.taxDocuments.map((d) =>
            d.id === taxForm.id
              ? { ...d, documentIds: d.documentIds.filter((x) => x !== docId) }
              : d
          ),
        };
        persistSnapshot(next);
        return next;
      });
    }
  };

  // ── Derived ──

  const accountsByCategory = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: frameData.financialAccounts.filter((a) => a.category === cat),
  }));

  const sortedTaxDocs = [...frameData.taxDocuments].sort((a, b) =>
    b.year.localeCompare(a.year)
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <SettlementStepPage
      step={step}
      phase="secure-inventory"
      overviewRoute="/sframe-143"
      hidePlannerBanner
      settlementDocumentContext={{ phase: PHASE, stepId: STEP_ID }}
    >
      {estateId ? (
        <div className="space-y-10 mb-8">

          {/* ── Discovery section ── */}
          <div className="rounded-xl border border-stone-200 bg-stone-50 overflow-hidden">
            <button
              type="button"
              onClick={() => setDiscoveryOpen((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
            >
              <div>
                <p className="text-sm font-semibold text-blue-900">Find Financial Accounts</p>
                <p className="text-xs text-stone-500 mt-0.5">
                  Use these steps to identify accounts that may not yet be documented.
                </p>
              </div>
              <svg
                className={`h-4 w-4 text-stone-400 transition-transform shrink-0 ${discoveryOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {discoveryOpen && (
              <div className="px-5 pb-5 space-y-3 border-t border-stone-200 pt-4">
                {DISCOVERY_ITEMS.map((item) => (
                  <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-stone-300 text-cyan-600 focus:ring-cyan-500 shrink-0"
                      checked={frameData.discoveryChecklist[item.key]}
                      disabled={isArchived}
                      onChange={() => toggleDiscovery(item.key)}
                    />
                    <span className="text-sm text-stone-700">{item.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* ── Financial Accounts section ── */}
          <div>
            <h2 className="text-lg font-semibold text-blue-900 mb-1">Financial Accounts</h2>
            <p className="text-sm text-stone-600 mb-5">
              Add and track all financial accounts. Select a category tile to add an account.
            </p>

            {/* Category tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              {CATEGORY_ORDER.map((cat) => {
                const count = frameData.financialAccounts.filter((a) => a.category === cat).length;
                return (
                  <button
                    key={cat}
                    type="button"
                    disabled={isArchived}
                    onClick={() => openNewAcct(cat)}
                    className="rounded-xl border border-stone-200 bg-white-alt p-4 text-left hover:border-cyan-400 hover:bg-cyan-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 disabled:opacity-50"
                  >
                    <p className="text-sm font-semibold text-stone-900">{CATEGORY_LABELS[cat]}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {count === 0 ? 'None added' : `${count} added`}
                    </p>
                    <p className="text-xs text-cyan-700 font-medium mt-2">+ Add</p>
                  </button>
                );
              })}
            </div>

            {/* Grouped accounts list */}
            <div className="space-y-6">
              {accountsByCategory.map(({ category, items }) =>
                items.length === 0 ? null : (
                  <div key={category}>
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                      {CATEGORY_LABELS[category]}
                    </h3>
                    <div className="space-y-2">
                      {items.map((acct) => (
                        <div
                          key={acct.id}
                          className="rounded-xl border border-stone-200 bg-white-alt p-4 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-stone-900 truncate">
                                  {acct.name}
                                </span>
                                <span
                                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${progressBadgeClass(acct.executorProgressStatus)}`}
                                >
                                  {progressLabel(acct.executorProgressStatus)}
                                </span>
                              </div>
                              <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                <span>{typeLabel(acct.category, acct.type)}</span>
                                {acct.institution && <span>{acct.institution}</span>}
                                {acct.lastFour && <span>···{acct.lastFour}</span>}
                                {acct.value != null && (
                                  <span>${acct.value.toLocaleString()}</span>
                                )}
                                <span>{valuationLabel(acct.valuationMethod)}</span>
                                <span>{accessLabel(acct.accessStatus)}</span>
                                <span>{actionLabel(acct.intendedAction)}</span>
                              </div>
                            </div>
                            <div className="flex gap-3 shrink-0 mt-0.5">
                              <button
                                type="button"
                                disabled={isArchived}
                                onClick={() => openEditAcct(acct)}
                                className="text-xs text-cyan-700 hover:underline disabled:opacity-50"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={isArchived}
                                onClick={() => deleteAcct(acct.id)}
                                className="text-xs text-red-600 hover:underline disabled:opacity-50"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* ── Tax Documents section ── */}
          <div>
            <h2 className="text-lg font-semibold text-blue-900 mb-1">Tax Documents & Records</h2>
            <p className="text-sm text-stone-600 mb-5">
              Upload and organize tax documents to understand the estate's tax situation and locate important records.
            </p>

            {/* Tax category tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {TAX_CATEGORY_ORDER.map((cat) => {
                const count = frameData.taxDocuments.filter((d) => d.category === cat).length;
                return (
                  <button
                    key={cat}
                    type="button"
                    disabled={isArchived}
                    onClick={() => openNewTax(cat)}
                    className="rounded-xl border border-stone-200 bg-white-alt p-4 text-left hover:border-cyan-400 hover:bg-cyan-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 disabled:opacity-50"
                  >
                    <p className="text-sm font-semibold text-stone-900">{TAX_CATEGORY_LABELS[cat]}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {count === 0 ? 'None added' : `${count} added`}
                    </p>
                    <p className="text-xs text-cyan-700 font-medium mt-2">+ Add</p>
                  </button>
                );
              })}
            </div>

            {/* Tax documents list */}
            {sortedTaxDocs.length > 0 && (
              <div className="space-y-3">
                {sortedTaxDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="rounded-xl border border-stone-200 bg-white-alt p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-stone-900">{doc.description}</span>
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${taxExecutorBadgeClass(doc.executorStatus)}`}
                          >
                            {taxExecutorLabel(doc.executorStatus)}
                          </span>
                        </div>
                        <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                          <span>Tax Year: {doc.year}</span>
                          <span>{taxStatusLabel(doc.status)}</span>
                          <span>{TAX_CATEGORY_LABELS[doc.category]}</span>
                          {doc.amount != null && <span>${doc.amount.toLocaleString()}</span>}
                        </div>
                        {doc.documentIds.length > 0 && (
                          <div className="mt-3">
                            <DocumentUploadSection
                              documents={docsByIds(doc.documentIds)}
                              disabled={true}
                              className="[&_h3]:text-xs"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3 shrink-0 mt-0.5">
                        <button
                          type="button"
                          disabled={isArchived}
                          onClick={() => openEditTax(doc)}
                          className="text-xs text-cyan-700 hover:underline disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={isArchived}
                          onClick={() => deleteTax(doc.id)}
                          className="text-xs text-red-600 hover:underline disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-grey-600 mb-6">Select an estate to get started.</p>
      )}

      {/* ── Financial Account Modal ── */}
      {acctForm && (
        <Modal
          open={acctModalOpen}
          title={isEditingAcct ? 'Edit Account' : `Add ${CATEGORY_LABELS[acctForm.category]}`}
          okText={isEditingAcct ? 'Save Changes' : 'Add Account'}
          cancelText="Cancel"
          onOk={saveAcct}
          onCancel={closeAcctModal}
          onClose={closeAcctModal}
          okDisabled={!acctForm.name.trim()}
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Type</label>
              <select
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.type}
                onChange={(e) => setAcctForm((f) => f && { ...f, type: e.target.value })}
              >
                {TYPE_OPTIONS[acctForm.category].map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Name / Account Label <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.name}
                placeholder="e.g. Chase Checking"
                onChange={(e) => setAcctForm((f) => f && { ...f, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Institution / Provider
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.institution ?? ''}
                placeholder="e.g. Chase Bank"
                onChange={(e) => setAcctForm((f) => f && { ...f, institution: e.target.value })}
              />
            </div>

            {acctForm.category !== 'income_source' && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Last 4 Digits</label>
                <input
                  type="text"
                  maxLength={4}
                  className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={acctForm.lastFour ?? ''}
                  placeholder="1234"
                  onChange={(e) =>
                    setAcctForm((f) => f && { ...f, lastFour: e.target.value.replace(/\D/g, '') })
                  }
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Value / Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.value ?? ''}
                placeholder="0.00"
                onChange={(e) =>
                  setAcctForm((f) => f && {
                    ...f,
                    value: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Valuation Method</label>
              <select
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.valuationMethod}
                onChange={(e) =>
                  setAcctForm((f) => f && { ...f, valuationMethod: e.target.value as ValuationMethod })
                }
              >
                <option value="statement_balance">Statement Balance</option>
                <option value="estimated">Estimated</option>
                <option value="market_value">Market Value</option>
                <option value="appraised">Appraised</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Access Status</label>
              <select
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.accessStatus}
                onChange={(e) =>
                  setAcctForm((f) => f && { ...f, accessStatus: e.target.value as AccessStatus })
                }
              >
                <option value="has_access">Has Access</option>
                <option value="no_access">No Access</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Intended Action</label>
              <select
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.intendedAction}
                onChange={(e) =>
                  setAcctForm((f) => f && { ...f, intendedAction: e.target.value as IntendedAction })
                }
              >
                <option value="transfer">Transfer</option>
                <option value="close">Close</option>
                <option value="claim">Claim</option>
                <option value="hold">Hold</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Executor Progress Status
              </label>
              <select
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={acctForm.executorProgressStatus}
                onChange={(e) =>
                  setAcctForm((f) => f && {
                    ...f,
                    executorProgressStatus: e.target.value as ExecutorProgressStatus,
                  })
                }
              >
                <option value="needs_review">Needs Review</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
              <textarea
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                rows={3}
                value={acctForm.notes ?? ''}
                placeholder="Any additional notes..."
                onChange={(e) => setAcctForm((f) => f && { ...f, notes: e.target.value })}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ── Tax Document Modal ── */}
      {taxForm && (
        <Modal
          open={taxModalOpen}
          title={isEditingTax ? 'Edit Tax Document' : `Add ${TAX_CATEGORY_LABELS[taxForm.category]}`}
          okText={isEditingTax ? 'Save Changes' : 'Add Document'}
          cancelText="Cancel"
          onOk={saveTax}
          onCancel={closeTaxModal}
          onClose={closeTaxModal}
          okDisabled={!taxForm.description.trim() || !taxForm.year.trim()}
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Tax Year <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={taxForm.year}
                placeholder="2023"
                onChange={(e) => setTaxForm((f) => f && { ...f, year: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={taxForm.description}
                placeholder="e.g. Federal Income Tax Return 2023"
                onChange={(e) => setTaxForm((f) => f && { ...f, description: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Status</label>
              <select
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={taxForm.status}
                onChange={(e) =>
                  setTaxForm((f) => f && { ...f, status: e.target.value as TaxDocumentStatus })
                }
              >
                <option value="filed">Filed</option>
                <option value="not_filed">Not Filed</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Amount (optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-stone-400 text-sm pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full rounded-lg border border-stone-300 pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={taxForm.amount ?? ''}
                  placeholder="0.00"
                  onChange={(e) =>
                    setTaxForm((f) => f && {
                      ...f,
                      amount: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Executor Status</label>
              <select
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                value={taxForm.executorStatus}
                onChange={(e) =>
                  setTaxForm((f) => f && { ...f, executorStatus: e.target.value as TaxExecutorStatus })
                }
              >
                <option value="needs_review">Needs Review</option>
                <option value="verified">Verified</option>
                <option value="missing">Missing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Notes (optional)</label>
              <textarea
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                rows={3}
                value={taxForm.notes ?? ''}
                placeholder="Any additional notes..."
                onChange={(e) => setTaxForm((f) => f && { ...f, notes: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Documents</label>
              <DocumentUploadSection
                documents={docsByIds(taxForm.documentIds)}
                disabled={isArchived || !estateId}
                onUpload={handleTaxDocUpload}
                onDelete={!isArchived ? handleTaxDocDelete : undefined}
                className="[&_h3]:text-xs"
              />
            </div>
          </div>
        </Modal>
      )}
    </SettlementStepPage>
  );
}
